/**
 * <cart-rec-carousel> — pagination pips and prev/next arrows for the cart's
 * "You may also like" row (snippets/custom-cart-recommendations.liquid).
 *
 * PROGRESSIVE ENHANCEMENT, not a carousel implementation: the row is a native
 * scroll-snap scroller and works fully without this file. All this element does
 * is reflect scroll position onto the server-rendered pips, scroll the row when
 * a pip or an arrow is clicked, and reveal the arrows. It composes no text —
 * every label is rendered (and translated) in Liquid.
 *
 * The two controls are revealed in OPPOSITE directions, deliberately:
 *   - pips ship visible and this element HIDES them when the cards already fit,
 *   - arrows ship `hidden` and this element SHOWS them when they don't.
 * An unenhanced pip still marks position on a row the shopper can swipe; an
 * unenhanced arrow is only a button that does nothing. So with JS off the row
 * keeps its pips and grows no arrows.
 *
 * Lives in the cart drawer, whose markup Dawn replaces wholesale on every cart
 * update: nothing observes the element from outside and it holds no state worth
 * preserving, so each re-render simply upgrades a fresh instance. Listeners are
 * removed in disconnectedCallback so discarded instances don't leak.
 */
if (!customElements.get('cart-rec-carousel')) {
  customElements.define(
    'cart-rec-carousel',
    class CartRecCarousel extends HTMLElement {
      connectedCallback() {
        this.row = this.querySelector('.cart-rec__row');
        this.pipList = this.querySelector('.cart-rec__pips');
        if (!this.row || !this.pipList) return;

        this.pips = [...this.pipList.querySelectorAll('.cart-rec__pip')];
        this.cards = [...this.row.querySelectorAll('.cart-rec__card')];
        if (this.pips.length === 0 || this.cards.length === 0) return;

        // Both rendered under the same `rec_kept > 1` guard as the pips, but
        // read defensively: this element must not throw on a row that has one.
        this.nav = this.querySelector('.cart-rec__nav');
        this.arrows = [...this.querySelectorAll('.cart-rec__arrow')];

        // Index of the card nearest the left edge. Set by update(), read by the
        // arrows — they step from where the shopper actually is, so an arrow
        // click after a swipe advances one card rather than jumping back.
        this.active = 0;

        this.onScroll = this.onScroll.bind(this);
        this.onPipClick = this.onPipClick.bind(this);
        this.onArrowClick = this.onArrowClick.bind(this);

        this.row.addEventListener('scroll', this.onScroll, { passive: true });
        this.pipList.addEventListener('click', this.onPipClick);
        this.nav?.addEventListener('click', this.onArrowClick);

        // Cards are fixed-width, so how many fit depends on the drawer width —
        // which changes with the viewport, not with any event we control.
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.row);

        this.update();
      }

      disconnectedCallback() {
        this.row?.removeEventListener('scroll', this.onScroll);
        this.pipList?.removeEventListener('click', this.onPipClick);
        this.nav?.removeEventListener('click', this.onArrowClick);
        this.resizeObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      onScroll() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }

      onPipClick(event) {
        const pip = event.target.closest('.cart-rec__pip');
        if (!pip) return;

        this.scrollToCard(Number(pip.dataset.index));
      }

      onArrowClick(event) {
        const arrow = event.target.closest('.cart-rec__arrow');
        if (!arrow || arrow.disabled) return;

        // Clamped rather than wrapped: the arrows are disabled at each end, so
        // there is nothing to wrap to, and a silent jump back to the first card
        // would read as the row having reset itself.
        const target = Math.min(this.cards.length - 1, Math.max(0, this.active + Number(arrow.dataset.dir)));
        this.scrollToCard(target);
      }

      scrollToCard(index) {
        const card = this.cards[index];
        if (!card) return;

        // scrollTo on the row itself, not scrollIntoView: the row sits inside
        // the drawer's own scroll container, which scrollIntoView would also
        // move — jumping the cart lines the shopper is looking at.
        this.row.scrollTo({
          left: card.offsetLeft - this.cards[0].offsetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      update() {
        // Nothing to page through when every card already fits.
        const overflows = this.row.scrollWidth - this.row.clientWidth > 1;
        this.pipList.hidden = !overflows;
        if (this.nav) this.nav.hidden = !overflows;
        if (!overflows) return;

        // Nearest card to the left edge, measured rather than derived from a
        // card width + gap constant, so the maths can't drift from the CSS.
        const origin = this.cards[0].offsetLeft + this.row.scrollLeft;
        let closest = Infinity;
        this.active = 0;
        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.offsetLeft - origin);
          if (distance < closest) {
            closest = distance;
            this.active = index;
          }
        });

        this.pips.forEach((pip, index) => {
          const current = index === this.active;
          pip.classList.toggle('cart-rec__pip--active', current);
          // aria-current on the button, so a screen reader reading the pip list
          // gets the position without us writing a sentence into the DOM.
          if (current) {
            pip.setAttribute('aria-current', 'true');
          } else {
            pip.removeAttribute('aria-current');
          }
        });

        // Ends measured from SCROLL POSITION, not from this.active: the last
        // card is usually only partly visible, so the row hits its maximum
        // scroll before that card's own offset is ever reached. Keying "next"
        // to the index would leave it live with nowhere left to go.
        const max = this.row.scrollWidth - this.row.clientWidth;
        this.arrows.forEach((arrow) => {
          arrow.disabled = Number(arrow.dataset.dir) < 0 ? this.row.scrollLeft <= 1 : this.row.scrollLeft >= max - 1;
        });
      }
    },
  );
}
